import { StepOrderType } from 'ts/types/config.types';
import { DecisionTreeDataResultModel } from 'redux/actions/caseCreationActions.types';
import routes from 'Routes/routesConfig';

const flagsToRoutes = {
  checkUp: '/check/up',
  additionalQuestions: '/additional-questions',
  incidentMore: routes.INCIDENT_MORE,
  synergyIncidentUnderstood: routes.SYNERGY_INCIDENT_UNDERSTOOD,
};

type FlagKeys = keyof typeof flagsToRoutes;
type Filters = Partial<Record<FlagKeys, boolean | undefined>>;

const shouldShowStep = (
  step: string,
  filters: Filters,
  flagsToRoutes: Record<FlagKeys, string>,
) => {
  const routesToHide = (
    Object.entries(filters) as unknown as [FlagKeys, boolean][]
  )
    .filter(([, value]) => !value)
    .map(([key]) => flagsToRoutes[key]);

  return !routesToHide.includes(step);
};

const removeStepFromSteps = (stepsOrder: StepOrderType, filters: Filters) => {
  return stepsOrder
    .filter(
      step =>
        Array.isArray(step) || shouldShowStep(step, filters, flagsToRoutes),
    )
    .map(step => {
      if (typeof step === 'string') {
        return step;
      }
      return step.filter(subStep =>
        shouldShowStep(subStep, filters, flagsToRoutes),
      );
    });
};

export const hasQuestionsForAccident = (
  decisionTreeResult: DecisionTreeDataResultModel,
) => {
  if (!decisionTreeResult?.is_accident) {
    return false;
  }
  return [
    decisionTreeResult?.is_car_in_mud,
    decisionTreeResult?.is_no_longer_on_wheels,
    decisionTreeResult?.is_police_on_its_way,
    decisionTreeResult?.is_thirdparty_involved,
    decisionTreeResult?.is_wheels_blocked_or_missing,
    decisionTreeResult?.type_of_damage,
  ].includes(true);
};

const hasFilters = (filters: Filters) =>
  Object.values(filters).filter(filter => filter !== undefined).length > 0;

/**
 * @param stepsOrder Steps
 * @param filters Object with steps we want to hide (only certain steps can be skipped)
 */
export const getStepsAndSubSteps = (
  stepsOrder: StepOrderType,
  filters?: Filters,
) => {
  const modifiedFilters = { ...filters };
  if (!modifiedFilters || !hasFilters(modifiedFilters)) {
    return stepsOrder;
  } else {
    return removeStepFromSteps([...stepsOrder], modifiedFilters);
  }
};
